import React, { useEffect, useMemo, useRef, useState } from 'react';
import CarouselComponent from '../../../Misc/CarouselComponent/CarouselComponent';
import styles from './styles.module.scss';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import { BarLoader, PuffLoader, PulseLoader, RiseLoader } from 'react-spinners';

import errorMsg from '../../../Functions/errorMsg';
import successMsg from '../../../Functions/successMsg';

import { faAd, faCheck, faEye, faFlag, faHeart, faLink, faLocationDot, faMessage, faPaperPlane, faPenToSquare, faPencil, faShare, faStar, faXmark, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import openAdManager from '../../../Functions/openAdManager';

import Image from 'next/image';
import { AnimatePresence, useInView } from 'framer-motion';
import { useRouter } from 'next/router';

import MapWithLocation from '../../../Misc/MapWithLocation/MapWithLocation';

import { motion } from 'framer-motion';
import OnlineProfile from '../../UserProfile/OnlineProfile/OnlineProfile';
import { useSocket } from '../../../Context/socket';

export default function BusinessListing({ listingID, closeListing }) {

    const { data: session, status } = useSession();
    const { socket, socketConnected } = useSocket();
    const router = useRouter();

    const [initialLoading, setInitialLoading] = useState(null);

    const [data, setData] = useState(null);
    const [posterProfile, setPosterProfile] = useState(null);
    const [reviewData, setReviewData] = useState(null);

    const [reported, setReported] = useState(false);

    const [listingReviews, setListingReviews] = useState(null);
    const [leftReview, setLeftReview] = useState(false);

    useEffect(() => {
        if(router === null || router === undefined) return;
        if(router.query === null || router.query === undefined) return;
        if(!router.query.openConversationID) return;
        closeListing(null);
      }, [router])

    useEffect(() => {
        if(initialLoading !== null) return;
        if(data !== null) return;
        if(status === 'loading') return;

        const requestingUser = status === 'unauthenticated' ? 'nouser' : session.user.token.username;

        async function getListingData() {
            setInitialLoading(true);

            const result = await axios.post(`${window.location.origin}/api/business/general/getListing`, {
                listingID: listingID,
                editData: false,
                requestingUser: requestingUser
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString())
                closeListing(null);
                setInitialLoading(false);
            })

            if(result && result.status === 200) {
                const listingData = result.data;
                setData(listingData.listing);
                setPosterProfile(listingData.profile);
                setReported(listingData.listing.reports.some((report) => report.reportingUser === requestingUser))
                setListingReviews(listingData.listing.reviews);
                setLeftReview(listingData.listing.reviews.some((review) => review.reviewer === session?.user?.token?.username))
                setReviewData(returnRating(listingData.listing.reviews));
            }
            setInitialLoading(false);
        }

        getListingData();

    }, [session, status])

    function isLoading() {
        if(status === 'loading') return true;
        if(initialLoading === true || initialLoading === null) return true;
        if(data === null) return true;
        if(posterProfile === null) return true;
        if(reviewData === null) return true;
        return false;
    }

    function authenticationIsOwnerCheck() {
        if(!session) return false;
        if(status !== 'authenticated') return false;
        if(session.user.token.username === data.owner) return false;
        return true;
      }

    const memoizeCarousel = useMemo(() => data !== null && data.images.length > 0 && <CarouselComponent listingImages={data.images} />, [data])
    const memoizedOptionBar = useMemo(() => <OptionBar listing={data} session={session} closeListing={closeListing} authenticationIsOwnerCheck={authenticationIsOwnerCheck} handleCreateReport={() => setCreateReport({listingID: data._id, reportingUser: data.owner})} reported={reported} />, [data, session, closeListing, reported])

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [viewUserProfile, openUserProfile] = useState(false);

    const [createReport, setCreateReport] = useState(null);

    const [loadingHasConvo, setLoadingHasConvo] = useState(false);
    const [hasConvo, setHasConvo] = useState(null);

    useEffect(() => {
        if(loadingHasConvo || hasConvo !== null) return;
        if(!session) return;
        if(status === 'loading') return;
        if(!data) return;
        if(!posterProfile) return;

        const controller = new AbortController();

        const profileUsername = posterProfile.username;

        async function getUserProfile() {
            setLoadingHasConvo(true);
            const result = await axios.post(`${window.location.origin}/api/messaging/hasActiveConvo`, {
                signal: controller.signal,
                requestingUserID: session.user.token.id,
                otherUser: profileUsername,
                convoType: 'Businesses',
                listingID: data._id
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString());
            })

            if(result && result.status === 200) {
                const data = result.data;
                setHasConvo({ hasConvo: data.hasConvo, convoID: data.convoID });
            }

            setLoadingHasConvo(false);
        }

        getUserProfile();

        return () => {
            controller.abort();
        }

    }, [status, posterProfile, data, posterProfile])

    const quickMessageTextRef = useRef(null);

    async function handleQuickMessageSend() {
        if(loadingHasConvo) return;

        if(!posterProfile) return errorMsg("There was an error sending your message, please try again later.");

        if(!session) return errorMsg("Please login to message this user.");
        if(status === 'loading') return;
        if(status === 'unauthenticated') return errorMsg("Please login to message this user.");

        if(!socket) return errorMsg("There was an error sending your message, please try again later.");
        if(!socketConnected) return errorMsg("There was an error sending your message, please try again later.");

        if(!quickMessageTextRef || !quickMessageTextRef.current) return;

        const messageText = quickMessageTextRef.current.value;

        if(messageText.length === 0) return errorMsg("Please enter a message before submitting.");
        if(messageText.length > 1000) return errorMsg("Your message is too long, please submit below 1000 characters.");

        const toUser = posterProfile.username;

        const listingID = data._id;

        const senderID = session.user.token.id;
        const sender = session.user.token.username;

        const basicConvoData = {
            type: 'Businesses',
            listingID: listingID,
            receiver: toUser,
            sender: sender,
            creationDate: new Date().toISOString()
        }

        setLoadingHasConvo(true);

        const result = await axios.post(`${window.location.origin}/api/messaging/create`, {
            conversationData: basicConvoData,
            messageText: messageText,
            senderID: senderID,
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        });

        if(result && result.status === 200) {
            const newConvoData = result.data;

            const hasNewConvoData = {
                hasConvo: true,
                convoID: newConvoData._id
            }

            setHasConvo(hasNewConvoData)

            socket.emit('createNewConversation', newConvoData)

            successMsg("Your message was sent.");

        }

        setLoadingHasConvo(false);

    }

    function handleOpenCurrentConversation() {
        if(!hasConvo) return errorMsg("Could not find this conversation, please try again later");
        if(hasConvo.hasConvo === false) return errorMsg("You do not have a conversation with this user.");

        const { query } = router;

        const updatedQuery = { ...query, openConversationID: hasConvo.convoID };

        router.push({ pathname: router.pathname, query: updatedQuery });
    }

    const [pendingReview, setPendingReview] = useState(false);

    const [selectedStars, setSelectedStars] = useState(null);
    const reviewMessageTextRef = useRef(null);

    async function createReview() {
        if(pendingReview) return;
        if(leftReview) return errorMsg("Seems you've already left a review, please refresh the page if the issue persists.");

        if(!reviewMessageTextRef || !reviewMessageTextRef.current) return;

        const messageText = reviewMessageTextRef.current.value;

        if(messageText.length === 0) return errorMsg("Please enter a message before submitting.");
        if(messageText.length > 1000) return errorMsg("Your review is too long, please submit below 1000 characters.");

        if(selectedStars === null) return errorMsg("Please select the desired star rating for this review.");

        setPendingReview(true);

        const result = await axios.post(`${window.location.origin}/api/business/reviews/createReview`, {
            requestingUserID: session.user.token.id,
            listingID: listingID,
            rating: selectedStars,
            message: messageText
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        });

        if(result && result.status === 200) {
            const newReview = result.data;
            setListingReviews([...listingReviews, newReview]);
            successMsg("Your review was submitted successfully!");
            setLeftReview(true);
        }

        setPendingReview(false);


    }


    const {displayTime, displayTimeType} = createdTimeStamp(data?.creationDate);

  return (
    <div id={styles.wrapper}>
        <AnimatePresence>
            { !reported && createReport && <CreateReportBox listingID={createReport.listingID} reportingUser={createReport.reportingUser} closeReport={() => setCreateReport(null)} successfulReport={() => setReported(true)} /> }
        </AnimatePresence>

        { viewUserProfile && <OnlineProfile profileUsername={posterProfile.username} closeProfile={() => openUserProfile(false)} /> }
        { isLoading() === true && <PulseLoader size={30} color='white' /> }
        { isLoading() === false && 
        <div id={styles.listing}>

            {memoizedOptionBar}

            <section id={styles.carouselContainer}>
                {memoizeCarousel}
                <div id={styles.listingInfoDots}>
                    <div style={{display: data.views.length === 0 ? 'none' : 'flex'}}>
                        <FontAwesomeIcon icon={faEye} id={styles.icon} />
                        <h1>{data.views.length}</h1>
                    </div>
                    <div style={{display: data.likes.length === 0 ? 'none' : 'flex'}}>
                        <FontAwesomeIcon icon={faHeart} id={styles.icon} />
                        <h1>{data.likes.length}</h1>
                    </div>
                </div>
            </section>

            <div id={styles.infoContainer}>

                <div id={styles.info}>
                    <h1>{data.title}</h1>
                    <h2>{data.category}</h2>
                    <h3>Listing posted {displayTime} {displayTimeType}</h3>
                    <hr />
                    <p>{data.description}</p>
                    <hr />
                </div>

                <div style={{marginBottom: '12px'}} id={styles.rentalPropertyLocation} onClick={() => { navigator.clipboard.writeText(`${data.propertyAddress}, ${data.propertyCity}, TX ${data.zip}`); successMsg("Property address copied to clipboard") }}>
                    <FontAwesomeIcon icon={faLocationDot} id={styles.icon} />
                    <div>
                        <h1>{data.propertyAddress}, {data.propertyCity}, TX {data.zip}</h1>
                        <h2>Click to copy the property address</h2>
                    </div>
                </div>

                <div style={{display: data.website.length === 0 ? 'none' : 'flex', marginBottom: '12px'}} id={styles.rentalPropertyLocation} onClick={() => window.open(data.website, '_blank')}>
                    <FontAwesomeIcon icon={faLink} id={styles.icon} />
                    <div>
                        <h1>{data.website}</h1>
                        <h2>External link provided by listing owner</h2>
                    </div>
                </div>

                <section id={styles.map}>
                    <MapWithLocation address={data.propertyAddress} city={data.propertyCity} zipCode={data.zip} />
                </section>
                <hr />

                <section id={styles.posterProfile}>
                    <div>
                        <span style={{display: profileLoaded === false ? 'flex' : 'none'}}> <PuffLoader color='#154734' size={50} /> </span>
                        <Image sizes='auto auto' fill src={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} alt={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} onLoadingComplete={() => setProfileLoaded(true)} />
                    </div>
                    <div>
                        <h1>{posterProfile.displayName}</h1>
                        { reviewData > 0 && <section>
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 1 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 2 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 3 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 4 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon icon={faStar} color={reviewData === 5 ? '#FFB81C' : 'lightgrey'} />
                        </section> }
                    </div>
                    <div>
                        <button onClick={() => openUserProfile(true)}>
                            <FontAwesomeIcon icon={faEye} id={styles.view} />
                        </button>
                    </div>
                </section>
                
                <section style={{display: authenticationIsOwnerCheck() ? 'flex' : 'none' }} id={styles.quickMessage}>
                    { !hasConvo || loadingHasConvo ?
                    <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}><BarLoader width={200} height={5} color='white' /></span>
                    :
                    <>
                        <div>
                            <FontAwesomeIcon icon={faMessage} id={styles.icon} />
                            <h1>{ !hasConvo.hasConvo ? 'Quick Message' : 'View Conversation' }</h1>
                        </div>
                        <div>
                            { !hasConvo.hasConvo && <textarea ref={quickMessageTextRef} placeholder="Write this user a message..." /> }
                            <FontAwesomeIcon onClick={!hasConvo.hasConvo ? handleQuickMessageSend : handleOpenCurrentConversation} icon={!hasConvo.hasConvo ? faPaperPlane : faEye} id={styles.icon} />
                        </div>
                    </>
                    }
                </section>
                
                <section id={styles.reviewWrapper}>
                    <section style={{display: authenticationIsOwnerCheck() ? 'flex' : 'none' }} id={styles.leaveReview}>
                        { !leftReview && pendingReview ?
                        <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}><BarLoader width={200} height={5} color='black' /></span>
                        :
                        <>
                            <div>
                                <FontAwesomeIcon icon={!leftReview ? faPenToSquare : faCheck} id={styles.icon} />
                                <h1>{ !leftReview ? 'Leave Review' : 'Thanks for your feedback!' }</h1>
                            </div>
                            <div style={{display: leftReview ? 'none' : 'flex'}}>
                                <section>
                                    <FontAwesomeIcon onClick={() => setSelectedStars(1)} id={styles.icon} icon={faStar} color={selectedStars >= 1 ? '#FFB81C' : 'lightgrey'} />
                                    <FontAwesomeIcon onClick={() => setSelectedStars(2)} id={styles.icon} icon={faStar} color={selectedStars >= 2 ? '#FFB81C' : 'lightgrey'} />
                                    <FontAwesomeIcon onClick={() => setSelectedStars(3)} id={styles.icon} icon={faStar} color={selectedStars >= 3 ? '#FFB81C' : 'lightgrey'} />
                                    <FontAwesomeIcon onClick={() => setSelectedStars(4)} id={styles.icon} icon={faStar} color={selectedStars >= 4 ? '#FFB81C' : 'lightgrey'} />
                                    <FontAwesomeIcon onClick={() => setSelectedStars(5)} id={styles.icon} icon={faStar} color={selectedStars === 5 ? '#FFB81C' : 'lightgrey'} />
                                </section>
                                <section style={{display: selectedStars === null ? 'none' : 'flex'}}>
                                    <textarea ref={reviewMessageTextRef} placeholder="Describe your experience..." />
                                    <FontAwesomeIcon onClick={() => createReview()} icon={faPencil} id={styles.icon} />
                                </section>

                            </div>
                        </>
                        }
                    </section>
                    <h1>Local Reviews <FontAwesomeIcon icon={faPenToSquare} id={styles.icon} /></h1>
                    <section id={styles.reviewsContainer}>
                        { listingReviews.length === 0 && <h1>This business has no reviews</h1> }
                        { listingReviews.length > 0 && listingReviews.map((review, index) => {
                            const { displayTime, displayTimeType } = createdTimeStamp(review.creationDate);
                            const reviewStars = review.stars;
                            return (
                                <div className={styles.reviewCard} key={index}>
                                    <section>
                                        <div><Image sizes='auto auto' fill objectFit='cover' src={review.reviewerPfp} alt={`Waco Exchange - Reviewer Pfp ${review.reviewer}`}  /></div>
                                        <span>
                                            <FontAwesomeIcon id={styles.icon} icon={faStar} color={reviewStars >= 1 ? '#FFB81C' : 'lightgrey'} />
                                            <FontAwesomeIcon id={styles.icon} icon={faStar} color={reviewStars >= 2 ? '#FFB81C' : 'lightgrey'} />
                                            <FontAwesomeIcon id={styles.icon} icon={faStar} color={reviewStars >= 3 ? '#FFB81C' : 'lightgrey'} />
                                            <FontAwesomeIcon id={styles.icon} icon={faStar} color={reviewStars >= 4 ? '#FFB81C' : 'lightgrey'} />
                                            <FontAwesomeIcon id={styles.icon} icon={faStar} color={reviewStars === 5 ? '#FFB81C' : 'lightgrey'} />
                                        </span>
                                        <h1>{review.reviewer}</h1>
                                        <h2>{displayTime} {displayTimeType}</h2>
                                    </section>
                                    <section>
                                        <p>{ review.message }</p>
                                    </section>
                                </div>
                            )
                        }) }
                    </section>
                </section>



                <AdvertisementSpot adID={'6595648'} session={session} router={router} closeListing={closeListing} />
            </div>

        </div>
        }

    </div>
  )
}

const CreateReportBox = ({ listingID, reportingUser, closeReport, successfulReport }) => {

    const { data: session, status } = useSession();

    const [pendingReport, setPendingReport] = useState(false);

    const reportTextRef = useRef(null);
    const [reportReason, setReportReason] = useState(null);

    const generateReport = async () => {
        if(listingID === null) return;
        if(pendingReport === true) return;
        if(!session) return;
        if(status !== 'authenticated') return;
        if(reportReason === null) return errorMsg("Please select a report reason.")
        if(reportTextRef === null || reportTextRef.current.value.length === 0) return errorMsg("Please enter some details about your report.");
        if(reportTextRef.current.value.length > 500) return errorMsg("Please enter some details about your report.");

        const reasonText = reportTextRef.current.value;

        setPendingReport(true);

        const result = await axios.post(`${window.location.origin}/api/TFSSystem/createReport`, {
            requestingUserID: session.user.token.id,
            origin: 'Businesses',
            reportedUser: reportingUser,
            reasonType: reportReason,
            message: reasonText,
            listingID: listingID,
            creationDate: new Date().toISOString()
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })
      
    
        if(result !== undefined) {
            if(result.status === 200) {
                closeReport();
                successfulReport();
                successMsg("Listing has been reported, thank you.");
            } else {
                errorMsg("We ran into an issue, please try again later.");
            }
        }
        setPendingReport(false);
    }

    return (
        <motion.section initial={{x: listingID === null ? '-320px' : '0'}} animate={{x: listingID === null ? '-320px' : '0', transition: { bounce: '0' }}} id={styles.reportBox}>
            <FontAwesomeIcon onClick={closeReport} icon={faXmark} id={styles.exit} />
            <h1>Create Report</h1>

            <div id={styles.types}>
                <button onClick={() => setReportReason("Inappropriate Content")} disabled={pendingReport} style={reportReason === "Inappropriate Content" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Inappropriate Content</button>
                <button onClick={() => setReportReason("Spam or Scams")} disabled={pendingReport} style={reportReason === "Spam or Scams" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Spam or Scams</button>
                <button onClick={() => setReportReason("Safety Concerns")} disabled={pendingReport} style={reportReason === "Safety Concerns" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Safety Concerns</button>
                <button onClick={() => setReportReason("Illegal Activity")} disabled={pendingReport} style={reportReason === "Illegal Activity" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Illegal Activity</button>
                <button onClick={() => setReportReason("Privacy Violation")} disabled={pendingReport} style={reportReason === "Privacy Violation" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Privacy Violation</button>
                <button onClick={() => setReportReason("Offensive Behavior")} disabled={pendingReport} style={reportReason === "Offensive Behavior" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Offensive Behavior</button>
            </div>

            <textarea maxLength={500} disabled={pendingReport} ref={reportTextRef} style={{display: reportReason !== null ? 'flex' : 'none'}} placeholder='Please give us some detail...' />
            <button disabled={pendingReport} onClick={generateReport} style={{display: reportReason !== null ? 'flex' : 'none'}}>
                {pendingReport ? <BarLoader color='white' /> : 'Submit Report'}
            </button>
        </motion.section>
    )
}

const OptionBar = ({ listing, session, closeListing, authenticationIsOwnerCheck, handleCreateReport, reported }) => {

    const [liked, setLiked] = useState(listing?.likes?.some(liker => session?.user?.token?.username === liker));
    const [likePending, setLikePending] = useState(false);

    async function apiHandleLike() {
        if(likePending === true) return;
        setLikePending(true);

        const username = session.user.token.username;
        const listingID = listing._id;

        if(liked === false) {
          const result = await axios.post(`${window.location.origin}/api/business/helpers/like`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })

          if(result && result.status === 200) {
            successMsg("Business added to my likes.");
            setLiked(true);
          }
          
        }
  
        if(liked === true) {
          const result = await axios.post(`${window.location.origin}/api/business/helpers/unlike`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })
      
          if(result && result.status === 200) {
            successMsg("Business removed from my likes.");
            setLiked(false);
          }
        }

        setLikePending(false)
    }

    return (
        <span id={styles.optionBar}>
            <span>
                { authenticationIsOwnerCheck() && <button id={styles.reportButton} onClick={() => reported === false && handleCreateReport()}> <FontAwesomeIcon icon={faFlag} id={styles.icon} color={reported === true ? '#FFB81C' : 'white'} /> </button> }
                { authenticationIsOwnerCheck() && <button id={styles.saveButton} style={{backgroundColor: liked === true ? '#CA0B00' : '#154734'}} onClick={() => apiHandleLike()}>

                { likePending === false && <FontAwesomeIcon icon={faHeart} id={styles.icon} color='white' /> }
                { likePending === true && <PuffLoader size={28} color='white' /> }

                </button> }
                <button id={styles.shareButton} onClick={() => {navigator.clipboard.writeText( `${window.location.origin}/marketplace/businesses?popup=sharedPost&_id=${listing._id}` ); successMsg("Link Copied") } } > <FontAwesomeIcon icon={faShare} id={styles.icon} /> </button>
                <button id={styles.exitButton} onClick={() => closeListing(null)} > <FontAwesomeIcon icon={faXmarkCircle} id={styles.icon} /> </button>
            </span>
        </span>
    )
}

const AdvertisementSpot = ({ adID, session, router, closeListing }) => {

    const adContainerRef = useRef(null);
  
    const adInView = useInView(adContainerRef, { once: true })
    const [adShown, setAdShown] = useState(false);
    const [adData, setAdData] = useState({ img: null, url: null });
  
    useEffect(() => {
      if(adInView === false) return;
      if(adShown === true) return;
      
      async function getAdImage() {
        setAdShown(true); // Ad was in view port
        setAdData({ img: true, url: null }); // Loading State
  
        const retrieveAdImg = await axios.post(`${window.location.origin}/api/admanager/retrieveAd`, {
          adID: adID
        }).catch((error) => {
          if(error?.response?.data?.status !== undefined) {
            console.error(error?.response?.data?.status);
          } else {
            console.error(error.toString());
          }
        })
  
        if(retrieveAdImg !== undefined) {
          if(retrieveAdImg.status === 200) {
            const img = retrieveAdImg.data.image; //img link
            const url = retrieveAdImg.data.url; //url link
            
            const structure = {
              img: img === 'NO_IMAGE' ? false : img,
              url: url
            }
  
            setAdData(structure);
          } else {
            console.error("There was an error when loading the advertisement image.");
          }
        }
      }
  
      getAdImage();
  
    }, [adInView])
  
    async function handleClick(e) {
      e.preventDefault();
      window.open(adData.url, '_blank');
  
      await axios.post(`${window.location.origin}/api/admanager/countClick`, {
        adID: adID
      }).catch((error) => {
        console.error(error.toString());
      })
    }
  
    const [coverLoaded, setCoverLoaded] = useState(false);
  
    return (
      <section ref={adContainerRef} id={styles.adSpot} style={{cursor: 'pointer', borderWidth: adData.img !== null && adData.img !== true & adData.img !== false && adData.img !== "NO_IMAGE" ? 0 : null}}>
        { adData.img === null && <h1>Ad Spot Available</h1> }
        { adData.img === false && <h1>Ad Spot Available</h1> }
        { adData.img === true && <RiseLoader size={15} color='#154734' /> }
        { (adData.img === null || adData.img === false) && session !== null && session !== undefined && <button onClick={() => { openAdManager(router); closeListing(null)} }><FontAwesomeIcon icon={faAd} id={styles.icon} /> Manager</button>}
  
        { adData.img !== null && adData.img !== true && adData.img !== false && <>
          <h6>Sponsored Advertisement</h6>
          <span style={{width: '100%', height: '100%', display: coverLoaded === true ? 'none' : 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <RiseLoader size={15} color='#154734' />
          </span>
          <Image onClick={(e) => handleClick(e)} sizes='auto auto' style={{objectFit: 'contain'}} fill src={adData.img} alt={adData.img} onLoadingComplete={() => setCoverLoaded(true)} />
        </> }
      </section>
    )
  }

/* Additional Functions */
/* ==================== */

function uppercaseFirst(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function returnRating(reviews) {
    let rating;
    let totalStars = 0;
    if(reviews?.length === 0) {
        rating = 0;
    } else {
        reviews?.forEach((review) => {
            totalStars+= review?.rating;
        })
        rating = totalStars / reviews?.length;
    }
    rating = Math.ceil(parseFloat(rating.toFixed(2)));

    return rating;
}

function createdTimeStamp(creationDate) {
    let displayTime;
    let displayTimeType;

    var delta = Math.floor(new Date().getTime() - new Date(creationDate).getTime());

    var seconds = Math.floor(delta / 1000);
    var minutes = Math.floor(seconds / 60);
    var hours =  Math.floor(minutes / 60);
    var days = Math.floor(hours / 24);
    var weeks = Math.floor(days / 7);
    var months = Math.floor(weeks / 4);

    if(seconds <= 10) {
        displayTime = "just now";
        displayTimeType = ""
    } else {
        displayTime = seconds;
        displayTimeType = "seconds ago"
    }

    if(minutes > 1) {
        displayTime = minutes;
        displayTimeType = 'minutes ago'
    }

    if(minutes === 1) {
        displayTime = minutes;
        displayTimeType = 'minute ago'
    }

    if(hours > 1) {
        displayTime = hours;
        displayTimeType = 'hours ago'
    }

    if(hours === 1) {
        displayTime = hours;
        displayTimeType = 'hour ago'
    }
    
    if(days > 1) {
        displayTime = days;
        displayTimeType = 'days ago'
    }

    if(days === 1) {
        displayTime = days;
        displayTimeType = 'day ago'
    }

    if(weeks > 1) {
        displayTime = weeks;
        displayTimeType = 'weeks ago'
    }

    if(weeks === 1) {
        displayTime = weeks;
        displayTimeType = 'week ago'
    }

    if(months > 1) {
        displayTime = months;
        displayTimeType = 'months ago'
    }

    if(months === 1) {
        displayTime = months;
        displayTimeType = 'month ago'
    }

    return {displayTime, displayTimeType}
}